/** Runtime slot map. Keep in sync with TOKEN-MAP.md § Slots. */

export type PaletteKey =
  | 'accent'
  | 'accent2'
  | 'accentInk'
  | 'bg'
  | 'surface'
  | 'surfaceAlt'
  | 'text'
  | 'textMuted'
  | 'line'

export type SlotKind = 'triple' | 'css'

export type ThemeSlotEntry = {
  custom: PaletteKey
  fallback: string
  kind: SlotKind
  /** ON path only: darken the palette colour before writing it (see `applySlots`). */
  darken?: true
}

export const THEME_SLOT_MAP: Record<string, ThemeSlotEntry> = {
  accent: {
    custom: 'accent',
    fallback: 'var(--colorPrimary)',
    kind: 'triple',
  },
  accentHover: {
    custom: 'accent2',
    fallback: 'var(--colorPrimaryHover)',
    kind: 'triple',
  },
  label: {
    custom: 'textMuted',
    fallback: 'var(--colorLabel)',
    kind: 'triple',
  },
  invoiceOptional: {
    custom: 'textMuted',
    fallback: '100, 116, 139',
    kind: 'triple',
  },
  checkoutHint: {
    custom: 'textMuted',
    fallback: '128, 128, 128',
    kind: 'triple',
  },
  navHome: {
    custom: 'line',
    fallback: 'rgb(195, 196, 207)',
    kind: 'css',
  },
  // Idle nav label + glyph: raw textMuted reads faded beside the accent tab, so darken it
  // (same weight as SmartWeb's bottomNavBarInactiveColor).
  navInactive: {
    custom: 'textMuted',
    fallback: '154, 156, 171',
    kind: 'triple',
    darken: true,
  },
  splitCardWash: {
    custom: 'text',
    fallback: '14, 14, 18',
    kind: 'triple',
  },
  onWashText: {
    custom: 'text',
    fallback: '0, 0, 0',
    kind: 'triple',
  },
  inverted: {
    custom: 'text',
    fallback: '26, 32, 44',
    kind: 'triple',
  },
  checkoutFoot: {
    custom: 'surfaceAlt',
    fallback: '238, 238, 238',
    kind: 'triple',
  },
  inputBg: {
    custom: 'surface',
    fallback: 'transparent',
    kind: 'css',
  },
  inputBorder: {
    custom: 'line',
    fallback: 'rgba(123, 125, 141, 0.25)',
    kind: 'css',
  },
  inputBorderHover: {
    custom: 'line',
    fallback: 'rgba(123, 125, 141, 0.5)',
    kind: 'css',
  },
  inputPlaceholder: {
    custom: 'textMuted',
    fallback: '46, 43, 36',
    kind: 'triple',
  },
  drawerBody: {
    custom: 'bg',
    fallback: '255, 255, 255',
    kind: 'triple',
  },
  drawerOptionCard: {
    custom: 'surface',
    fallback: 'rgba(0, 0, 0, 0.05)',
    kind: 'css',
  },
  drawerOptionCardHover: {
    custom: 'surfaceAlt',
    fallback: 'rgba(0, 0, 0, 0.1)',
    kind: 'css',
  },
  tipMostTippedIdle: {
    custom: 'surfaceAlt',
    fallback: '204, 204, 204',
    kind: 'triple',
  },
  tipMostTippedIdleInk: {
    custom: 'text',
    fallback: '255, 255, 255',
    kind: 'triple',
  },
  hairline: {
    custom: 'line',
    fallback: 'rgba(123, 125, 141, 0.25)',
    kind: 'css',
  },
  resultCard: {
    custom: 'surface',
    fallback: '46, 43, 36',
    kind: 'triple',
  },
  resultCardInk: {
    custom: 'text',
    fallback: '255, 255, 255',
    kind: 'triple',
  },
  outlineBorder: {
    custom: 'line',
    fallback: 'rgb(var(--colorPrimary))',
    kind: 'css',
  },
  currencyCode: {
    custom: 'text',
    fallback: 'var(--colorLabel)',
    kind: 'triple',
  },
  noImageLight: {
    custom: 'surfaceAlt',
    fallback: '223, 223, 232',
    kind: 'triple',
  },
  noImageSoft: {
    custom: 'line',
    fallback: 'rgb(230, 231, 237)',
    kind: 'css',
  },
}

export const PALETTE_CSS_VARS: Record<Exclude<PaletteKey, 'line'>, string> = {
  accent: '--colorAccent',
  accent2: '--colorAccent2',
  accentInk: '--colorAccentInk',
  bg: '--colorBg',
  surface: '--colorSurface',
  surfaceAlt: '--colorSurfaceAlt',
  text: '--colorText',
  textMuted: '--colorTextMuted',
}

export const LINE_CSS_VAR = '--colorLine'
export const RADIUS_CSS_VARS = {
  sm: '--radiusSm',
  md: '--radiusMd',
  lg: '--radiusLg',
  pill: '--radiusPill',
} as const

/** Radius slots whose OFF px ≠ the Studio key used on ON (e.g. inputs 12px → lg). */
export const RADIUS_SLOT_MAP: Record<string, { custom: keyof typeof RADIUS_CSS_VARS; fallback: string }> = {
  inputRadius: {
    custom: 'lg',
    fallback: '12px',
  },
}

/** Non-color slots whose ON value is a CSS literal (not a palette lookup). */
export const CSS_SLOT_MAP: Record<string, { custom: string; fallback: string }> = {
  currencyCodeWeight: {
    custom: '600',
    fallback: 'inherit',
  },
  currencyCodeSize: {
    custom: '1em',
    fallback: 'inherit',
  },
}

/** Theme ON overwrites these. Scale sizes (--fontSize9 …) stay :root-only. */
export const FONT_FAMILY_CSS_VARS = {
  body: '--fontBody',
  display: '--fontDisplay',
} as const

export const FONT_SIZE_CSS_VARS = {
  body: '--fontSizeBody',
  header: '--fontSizeHeader',
} as const

export const FONT_WEIGHT_CSS_VARS = {
  body: '--fontWeightBody',
  display: '--fontWeightDisplay',
} as const

export const FONT_TRACKING_DISPLAY = '--fontTrackingDisplay'
export const FONT_LINE_HEIGHT_DISPLAY = '--lineHeightDisplay'
